import { Clerk } from '@clerk/clerk-js';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!publishableKey) {
  console.warn('Clerk publishable key not configured');
}

// Headless Clerk instance — statsmatic's auth flows live in this service
// layer rather than React components, so the imperative clerk-js API is a
// better fit than @clerk/clerk-react.
export const clerk = new Clerk(publishableKey || 'pk_test_placeholder');

let _loadPromise: Promise<void> | null = null;

export function clerkReady(): Promise<void> {
  if (!_loadPromise) {
    _loadPromise = clerk.load({ standardBrowser: true });
  }
  return _loadPromise;
}

export interface SignUpData {
  email: string;
  password: string;
  organizationName?: string;
}

export interface SignInData {
  email: string;
  password: string;
}

/**
 * Send the new-device verification code.
 *
 * supportedSecondFactors lists ENROLLED MFA factors; a device-trust challenge
 * is not MFA enrolment, so that array can be empty here. Requiring an
 * emailAddressId would reject the very case this exists to serve, so let Clerk
 * resolve the address itself when there is no factor to read it from.
 */
async function prepareSecondFactorEmailCode(signIn: any) {
  const emailFactor = (signIn.supportedSecondFactors || []).find(
    (f: any) => f.strategy === 'email_code'
  );
  await signIn.prepareSecondFactor(
    emailFactor
      ? { strategy: 'email_code', emailAddressId: emailFactor.emailAddressId }
      : { strategy: 'email_code' }
  );
}

export const authService = {
  async signUp({ email, password }: SignUpData) {
    await clerkReady();
    const signUp = await clerk.client!.signUp.create({
      emailAddress: email,
      password,
    });

    if (signUp.status === 'complete') {
      await clerk.setActive({ session: signUp.createdSessionId });
      return { needsEmailConfirmation: false };
    }

    // Email verification required — send the code; the signup flow's
    // verification step calls verifySignupCode with it
    await signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
    return { needsEmailConfirmation: true };
  },

  async verifySignupCode(code: string) {
    await clerkReady();
    const signUp = clerk.client!.signUp;
    const result = await signUp.attemptEmailAddressVerification({ code });
    if (result.status === 'complete') {
      await clerk.setActive({ session: result.createdSessionId });
      return true;
    }
    throw new Error('Verification failed. Please check your code and try again.');
  },

  async resendSignupCode() {
    await clerkReady();
    await clerk.client!.signUp.prepareEmailAddressVerification({ strategy: 'email_code' });
  },

  async signIn({ email, password }: SignInData) {
    await clerkReady();
    if (clerk.session) {
      // Already signed in (Clerk rejects a new sign-in attempt) — treat as
      // success; callers proceed to verifyAuth with the active session
      return { session: clerk.session };
    }
    const signIn = await clerk.client!.signIn.create({
      identifier: email,
      password,
      strategy: 'password',
    });

    // New-device verification: Clerk emails a code for a client it has not
    // seen before. Two names for the same check — instances created after
    // Nov 2025 report needs_client_trust, older ones the legacy
    // needs_second_factor. Statsmatic's instance dates from the Aug 2026
    // migration, so only the former ever arrives.
    //
    // Cast because clerk-js 5's bundled @clerk/types predates device trust
    // and omits the status from SignInStatus; the runtime does send it.
    const signInStatus = signIn.status as string | null;
    if (
      signInStatus === 'needs_client_trust' ||
      signInStatus === 'needs_second_factor'
    ) {
      await prepareSecondFactorEmailCode(signIn);
      return { session: null, needsSecondFactor: true };
    }

    if (signIn.status !== 'complete') {
      console.error('Clerk sign-in incomplete:', signIn.status, signIn);
      throw new Error(
        `Sign in could not be completed (status: ${signIn.status}). Please try again.`
      );
    }
    await clerk.setActive({ session: signIn.createdSessionId });
    return { session: clerk.session };
  },

  async verifySecondFactorCode(code: string) {
    await clerkReady();
    const result = await clerk.client!.signIn.attemptSecondFactor({
      strategy: 'email_code',
      code,
    });
    if (result.status !== 'complete') {
      throw new Error('Verification failed. Please check your code and try again.');
    }
    await clerk.setActive({ session: result.createdSessionId });
    return { session: clerk.session };
  },

  async resendSecondFactorCode() {
    await clerkReady();
    await prepareSecondFactorEmailCode(clerk.client!.signIn);
  },

  async signOut() {
    await clerkReady();
    try {
      await Promise.race([
        clerk.signOut(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('signOut timed out')), 4000)
        ),
      ]);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  },

  async getSession() {
    await clerkReady();
    return clerk.session ?? null;
  },

  async getToken(): Promise<string | null> {
    await clerkReady();
    if (!clerk.session) return null;
    // Clerk tokens expire after 60s; getToken() returns a fresh one and
    // handles refresh internally — never cache the string
    return clerk.session.getToken();
  },

  async getUser() {
    await clerkReady();
    return clerk.user ?? null;
  },

  // Password reset is a three-step Clerk sign-in flow:
  // resetPassword(email) -> verifyResetCode(code) -> updatePassword(newPassword)
  async resetPassword(email: string) {
    await clerkReady();
    await clerk.client!.signIn.create({
      strategy: 'reset_password_email_code',
      identifier: email,
    });
  },

  async verifyResetCode(code: string) {
    await clerkReady();
    const result = await clerk.client!.signIn.attemptFirstFactor({
      strategy: 'reset_password_email_code',
      code,
    });
    if (result.status !== 'needs_new_password' && result.status !== 'complete') {
      throw new Error('Invalid verification code');
    }
  },

  async updatePassword(newPassword: string) {
    await clerkReady();
    const result = await clerk.client!.signIn.resetPassword({
      password: newPassword,
      signOutOfOtherSessions: true,
    });
    if (result.status === 'complete') {
      await clerk.setActive({ session: result.createdSessionId });
    }
  },

  onAuthStateChange(callback: (event: string, session: unknown) => void) {
    let hadSession: boolean | null = null;
    const unsubscribe = clerk.addListener(({ session }) => {
      const has = !!session;
      if (hadSession === null) {
        hadSession = has;
        return;
      }
      if (has !== hadSession) {
        hadSession = has;
        callback(has ? 'SIGNED_IN' : 'SIGNED_OUT', session);
      }
    });
    // Match the supabase return shape used by callers
    return { data: { subscription: { unsubscribe } } };
  },
};

export default authService;
